import React, { useState, useEffect } from 'react';
import { Image, message } from 'antd';
import { useCookies } from 'react-cookie';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { theme } from 'utils/style/theme';
import isDownload from 'hooks/isDownload';
import TableToolbar from 'components/table/TableToolbar';
import MainTable from 'components/table/MainTable';
import NoImage from 'components/etc/NoImage';
import DefaultButton from 'components/button/DefaultButton';
import IllustRegisterModal from './IllustRegisterModal';
import { IIllust } from 'utils/types/resourceTypes';
import { illustApi } from 'apis/resource/illustApis';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Edit2, ReceiveSquare, Trash } from 'iconsax-react';
import ActionButton from 'components/table/ActionButton';
import SelectMultiple from '../_component/SearchMultiple';
import iconSearch from 'utils/icon/icon_search.svg';

import { characterApis } from 'apis/resource/characterApis';
import { handleMessage } from 'utils/helper/message';
import DeleteModal from 'components/modal/DeleteModal';
import CheckboxStyled from 'components/CheckboxStyled';
import { ICommonCode } from 'utils/types/storyTypes';
/**
 * * 리소스 - 일러스트 목록
 */

interface IProps {
  selectProject: any;
}

const IllustTable = ({ selectProject }: IProps) => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies] = useCookies();
  const { lang } = cookies;

  const [visible, setVisible] = useState({
    register: false,
    zip: false,
    delete: false,
    name: '',
  }); // 모달 show on/off

  const [refresh, setRefresh] = useState<boolean>(false);
  const [selectedData, setSelectedData] = useState<IIllust | null>(null);
  const [illustData, setillustData] = useState<IIllust[] | null>(null);
  const [tableData, setTableData] = useState<IIllust[] | null>(null);
  const [charactersData, setCharacterData] = useState<ICommonCode[] | null>(
    null,
  );

  const [searchData, setSearchData] = useState<string[]>([]);

  const [selectCharacterData, setSelectCharacterData] = useState<
    ICommonCode[] | null
  >(null);

  useEffect(() => {
    if (charactersData) {
      setSelectCharacterData(charactersData);
    }
  }, [charactersData]);

  const fetchData = async () => {
    if (projectId) {
      try {
        const resIllustData = await illustApi.illustList(projectId);
        setillustData(resIllustData.list);
        const characterRes = await characterApis.list(projectId.toString());
        setCharacterData(characterRes);
      } catch (err) {
        console.log('Fetch data error: ', err);
      }
    }
  };
  useEffect(() => {
    fetchData();
  }, [refresh]);

  useEffect(() => {
    if (illustData) {
      setTableData(illustData);
    }
  }, [illustData]);

  // 모달 열기
  const handleOpen = (e) => {
    setVisible({
      ...visible,
      register: true,
    });
  };

  /// 수정 이동
  const handleLink = (record) => {
    navigate(`${record.id}/update`, {
      state: {
        record: record,
        project_type: location?.state?.record?.project_type,
      },
    });
  };

  const handleClickDeleteModal = (record) => {
    setSelectedData(record.id);
    setVisible({
      ...visible,
      delete: true,
      name: record?.image_name,
    });
  };

  /// 다운로드
  const handleDownload = (record) => {
    // 다운로드 함수로 보내준다.
    isDownload(record);
  };

  // handle Delete
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const handleDelete = async () => {
    if (selectedData) {
      try {
        setDeleteLoading(true);
        await illustApi.illustDelete(selectedData.toString());
        setRefresh(!refresh);
        setVisible({ ...visible, delete: false });
        setDeleteLoading(false);
      } catch (err) {
        handleMessage(message, 'error', 'Please try again');
        setDeleteLoading(false);
      }
    }
  };

  // handle search input onChange
  const handleSelectChange = (value: any, data: any) => {
    setSearchData(value);
  };
  useEffect(() => {
    const cloneData = JSON.parse(JSON.stringify(illustData));
    if (cloneData) {
      if (searchData.length) {
        const newArray = cloneData.filter((obj) =>
          searchData.includes(obj.speaker),
        );
        setTableData(newArray);
      } else {
        setTableData(illustData);
      }
    }
  }, [searchData]);
  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <ReceiveSquare size='24' onClick={() => handleDownload(record)} />
        ),
        hoverColor: theme.colors.green_500,
        color: theme.colors.dark_200,
      },
      {
        key: 2,
        item: <Edit2 size='24' onClick={() => handleLink(record)} />,
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 3,
        item: (
          <Trash
            size='24'
            onClick={() => {
              setSelectedData(record);
              handleClickDeleteModal(record);
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: '3rem',
      sorter: (a, b) => a.minicut_id - b.minicut_id,
      align: 'center',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.table.preview',
      ).toUpperCase(),
      dataIndex: 'image_url',
      width: '4rem',
      align: 'center',
      render: (text, record) =>
        record.image_url === '' ? (
          <NoImage />
        ) : (
          <Image
            src={record.image_url}
            alt={`${record.image_name}`}
            width={100}
            height={80}
            style={{ objectFit: 'cover', margin: 'auto' }}
          />
        ),
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.table.imageName',
      ).toUpperCase(),
      dataIndex: 'image_name',
      width: '7rem',
      sorter: (a, b) => {
        if (a.image_name && b.image_name) {
          return a.image_name.localeCompare(b.image_name, undefined, {
            sensitivity: 'base',
          });
        } else if (a.image_name && !b.image_name) {
          return -1;
        } else if (!a.image_name && b.image_name) {
          return 1;
        } else {
          return 0;
        }
      },
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.table.title',
      ).toUpperCase(),
      dataIndex: 'localizations',
      width: '8rem',
      ellipsis: true,
      render: (localizations, record) => {
        const findTitleName = localizations?.find(
          (e) => e.lang === cookies.lang,
        );
        return (
          <>
            {localizations.map((ele) => (
              <p className='content-data'>{findTitleName?.public_name}</p>
            ))}
          </>
        );
      },
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.table.xOffset',
      ).toUpperCase(),
      dataIndex: 'offset_x',
      align: 'center',
      width: '3rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.table.yOffset',
      ).toUpperCase(),
      dataIndex: 'offset_y',
      align: 'center',
      width: '3rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.table.character',
      ).toUpperCase(),
      dataIndex: 'speaker',
      sorter: (a, b) => {
        if (a.speaker && b.speaker) {
          return a.speaker.localeCompare(b.speaker, undefined, {
            sensitivity: 'base',
          });
        } else if (a.speaker && !b.speaker) {
          return -1;
        } else if (!a.speaker && b.speaker) {
          return 1;
        } else {
          return 0;
        }
      },

      width: '3rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.table.gameScale',
      ).toUpperCase(),
      dataIndex: 'game_scale',
      align: 'center',
      width: '4rem',
    },
    {
      title: t('constants.publicState.public').toUpperCase(),
      dataIndex: 'is_public',
      align: 'center',
      width: '4rem',
      render: (_) => {
        return (
          <div className='flex-center'>
            <CheckboxStyled isChecked={_ ? true : false} />
          </div>
        );
      },
    },
    {
      dataIndex: 'move',
      width: '8rem',
      render: (text, record) => {
        const menus = [tableActionMenu(record)];
        return <ActionButton menus={menus} />;
      },
    },
  ];

  return (
    <>
      <TableToolbar>
        <SelectMultiple
          optionList={selectCharacterData?.map((e) => {
            return {
              value: e.code,
              label: e.code_name,
            };
          })}
          placeholder='Search...'
          mode='tags'
          onChange={handleSelectChange}
          width='320px'
          value={searchData}
          setValue={setSearchData}
        />
        <div>
          <DefaultButton
            name='register'
            onClick={handleOpen}
            disabled={selectProject?.auth}
          >
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.miniCutContent.header.registerMinicut',
            )}
          </DefaultButton>
        </div>
      </TableToolbar>
      {tableData && (
        <TableWrapper>
          <MainTable columns={columnsOrigin} dataSource={tableData} />
        </TableWrapper>
      )}

      <IllustRegisterModal
        visible={visible}
        setVisible={setVisible}
        refresh={refresh}
        setRefresh={setRefresh}
      />
      <DeleteModal
        visible={visible.delete}
        setVisible={setVisible}
        title={t(
          'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.illustrationContent.illustrationDeleteModal.title',
        )}
        label={visible.name}
        handleDelete={handleDelete}
        loading={deleteLoading}
      />
    </>
  );
};

export default IllustTable;

const TableWrapper = styled.div`
  .ant-table-thead > tr > th {
    padding: 0.8rem 0.8rem;
  }
  .ant-image {
    margin: auto;
  }
  .content-data,
  .ant-table-thead > tr > th,
  .ant-table-cell,
  .ant-table-column-title {
    font-size: ${theme.fontSizes.small};
    color: ${theme.colors.dark_300} !important;
    font-weight: ${theme.fontWeights.bold};
    line-height: 16px;
    letter-spacing: 0.006rem;
  }
  .icon {
    cursor: pointer;
    width: 20px;
    height: 20px;
  }
  .icon-delete-outline,
  .icon-edit-outline {
    margin-left: 18px;
  }
`;
